import { Component } from 'react';
import { connect } from 'react-redux';
import { selectTripadvisorResults, clearResults as clearTripadvisorResults } from '../../redux/tripadvisorSlice';
import TripadvisorCategorySelector from '../TripadvisorCategorySelector';
import TripadvisorSearch from '../TripadvisorSearch';
import TripadvisorResultsList from '../TripadvisorResultsList';
import * as dbConstants from '../../dao/dbConstants';
import { INPUT_LAYOUT_TYPE_SEPARATE_LABEL } from '../componentConstants';
import { decodeHtmlEntities } from '../../utils/utils';

class TripAdvisorInput extends Component {
  constructor(props) {
    super(props);
    const { initialSearchTerm = '' } = props;
    
    this.state = {
      selectedResult: null,
      isSearching: false,
      hasSearched: false,
      searchTerm: initialSearchTerm,
    };
  }

  initializeFromRecord(record, forceRecordUpdate, onChange) {
    if (record?.articleTypeId === dbConstants.ARTICLE_TYPE_TRIPADVISOR) {
      console.log('TripAdvisorInput initializing with record:', {
        id: record.id,
        locationId: record.locationId,
        metadataLocationId: record?.metadata?.locationId,
        source: record.source,
        metadata: record.metadata,
        hasDisplayData: !!record?.metadata?.displayData
      });
    }
    
    // Initialize with existing location if available
    // Note: locationId is stored in metadata for TripAdvisor articles
    const locationId = record?.metadata?.locationId || record?.locationId;
    const displayData = record?.metadata?.displayData;
    
    if (locationId && displayData) {
      console.log('Setting selectedResult with locationId:', locationId);
      
      // CRITICAL: Always restore locationId to the record for validation
      if (!record.locationId && locationId) {
        console.log('Restoring locationId from metadata to record');
        if (forceRecordUpdate) {
          forceRecordUpdate('locationId', locationId);
        } else if (onChange) {
          onChange(locationId, 'locationId');
        }
      }
      
      this.setState({
        selectedResult: {
          id: locationId,
          name: displayData.name || record.source,
          displayData: displayData
        },
        searchTerm: record?.metadata?.tripadvisorSearchKeyword || record?.source || '',
      });
    }
    
    const category = record?.tripadvisorCategory || record?.metadata?.category;
    if (record?.articleTypeId === dbConstants.ARTICLE_TYPE_TRIPADVISOR && !category) {
      if (forceRecordUpdate) {
        forceRecordUpdate('tripadvisorCategory', 'geos');
      }
    } else if (record?.articleTypeId === dbConstants.ARTICLE_TYPE_TRIPADVISOR && category && !record?.tripadvisorCategory) {
      if (forceRecordUpdate) {
        forceRecordUpdate('tripadvisorCategory', category);
      }
    }
  }

  componentDidMount() {
    const { record, forceRecordUpdate, onChange, initialCategory } = this.props;
    
    if (this.props.clearTripadvisorResults) {
      this.props.clearTripadvisorResults();
    }
    
    if (initialCategory && forceRecordUpdate) {
      forceRecordUpdate('tripadvisorCategory', initialCategory);
    }
    
    this.initializeFromRecord(record, forceRecordUpdate, onChange);
  }

  componentDidUpdate(prevProps) {
    const { record, forceRecordUpdate, onChange } = this.props;
    
    if (record?.id !== prevProps.record?.id || 
        record?.metadata !== prevProps.record?.metadata) {
      console.log('TripAdvisorInput record updated, reinitializing...');
      this.initializeFromRecord(record, forceRecordUpdate, onChange);
    }
  }

  componentWillUnmount() {
    if (this.props.clearTripadvisorResults) {
      this.props.clearTripadvisorResults();
    }
  }

  handleCategoryChange = (value) => {
    const { onChange, forceRecordUpdate } = this.props;
    
    if (forceRecordUpdate) {
      forceRecordUpdate('tripadvisorCategory', value);
    } else if (onChange) {
      onChange(value, 'tripadvisorCategory');
    }
  };

  handleSearchChange = (value) => {
    const { onChange, forceRecordUpdate } = this.props;
    
    this.setState({ searchTerm: value });
    
    if (forceRecordUpdate) {
      forceRecordUpdate('source', value);
    } else if (onChange) {
      onChange(value, 'source');
    }
    
    if (this.state.selectedResult) {
      this.setState({ 
        selectedResult: null,
        isSearching: true,
      });
      
      if (forceRecordUpdate) {
        forceRecordUpdate('locationId', null);
      }
    } else {
      this.setState({ isSearching: true });
    }
    
    if (this.props.searchResults && this.props.searchResults.length > 0) {
      this.setState({ hasSearched: true });
    }
  };

  handleResultSelect = (result) => {
    const { onChange, onFieldError, forceRecordUpdate, onBlur, record } = this.props;
    const autoPopulateNameField = this.props.autoPopulateNameField || this.props.input?.autoPopulateNameField;
    
    if (result && result.id && onChange) {
      try {
        if (typeof result.id !== 'string' && typeof result.id !== 'number') {
          return;
        }
        
        this.setState({ 
          selectedResult: {
            ...result,
            displayData: record?.metadata?.displayData
          }
        });
        if (autoPopulateNameField && (!record.name || !record.name.trim()) && result.name) {
          const decodedName = decodeHtmlEntities(result.name);
          if (forceRecordUpdate) {
            forceRecordUpdate('name', decodedName);
          } else if (onChange) {
            onChange(decodedName, 'name');
          }
        }
        
        // No need to save searchTerm to metadata manually
        // The backend will use the 'source' field value as tripadvisorSearchKeyword
        
        // Clear any validation errors BEFORE updating the location
        if (onFieldError) {
          onFieldError('locationId', null);
        }
        
        if (forceRecordUpdate) {
          forceRecordUpdate('locationId', result.id);
          
          // Keep the search term in source field, not the hotel name
          // The backend will use this for tripadvisorSearchKeyword
          if (this.state.searchTerm) {
            forceRecordUpdate('source', this.state.searchTerm);
          }
          
          const currentCategory = record?.tripadvisorCategory || 'geos';
          forceRecordUpdate('tripadvisorCategory', currentCategory);
        } else {
          onChange(result.id, 'locationId');
          
          if (this.state.searchTerm) {
            onChange(this.state.searchTerm, 'source');
          }
          
          const currentCategory = record?.tripadvisorCategory || 'geos';
          onChange(currentCategory, 'tripadvisorCategory');
        }
        
        // Give time for state updates to complete, then trigger validation
        setTimeout(() => {
          if (onBlur) {
            onBlur('locationId', result.id);
          }
        }, 150);
      } catch {
        if (window.location.hostname !== 'localhost') {
          console.error('Error during TripAdvisor selection');
        }
      }
    }
  };

  render() {
    const { record, searchResults, layoutType, isDisabled, showPreview = true, stickyHeader = true } = this.props;
    
    const { selectedResult, searchTerm } = this.state;
    
    if (record?.articleTypeId !== dbConstants.ARTICLE_TYPE_TRIPADVISOR) {
      return null;
    }
    
    const category = record?.tripadvisorCategory || record?.metadata?.category || 'geos';
    const searchValue = searchTerm;
    const shouldShowResults = searchResults && searchResults.length > 0;
    
    // Check if we're editing an existing article
    // An article is in edit mode if it has any of these:
    // 1. An ID in the record
    // 2. Metadata with saved data
    // 3. A locationId in metadata (indicates it was previously saved)
    const isEditMode = !!(
      record?.id || 
      record?.metadata?.locationId ||
      record?.metadata?.displayData
    );
    
    return (
      <div className={`tripadvisor-input ${layoutType === INPUT_LAYOUT_TYPE_SEPARATE_LABEL ? 'separate-label' : 'inline'}`}>
        {}
        <div className="tripadvisor-category-section">
          <TripadvisorCategorySelector
            label="Category"
            value={category}
            onChange={this.handleCategoryChange}
            disabled={isEditMode}
          />
        </div>
        
        {}
        <div className="tripadvisor-search-section" style={{ marginTop: '15px' }}>
          <label>
            Search Location
            <span className="required">*</span>
          </label>
          <TripadvisorSearch
            value={searchValue}
            onChange={this.handleSearchChange}
            category={category}
            isReadOnly={!!selectedResult || isEditMode}
            disabled={isDisabled || isEditMode}
          />
          {/* Validation errors are handled at form submission, not inline */}
        </div>
        
        {}
        {(shouldShowResults || (selectedResult && selectedResult.displayData)) && (
          <div className="tripadvisor-results-section" style={{ marginTop: '15px' }}>
            <div style={{ 
              padding: '10px', 
              marginBottom: '10px', 
              backgroundColor: '#f8f9fa', 
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '12px',
              color: '#666'
            }}>
              <strong>Note:</strong> {isEditMode ? 
                'This is the saved location for this article. Location cannot be changed after creation.' :
                (selectedResult && selectedResult.displayData ? 
                  'This is the saved location. You can search for a different location if needed.' : 
                  'Select a location from the results below. The category above must be selected first.')}
            </div>
            <TripadvisorResultsList
              selectedResult={selectedResult}
              onSelect={isEditMode ? undefined : this.handleResultSelect}
              onChange={this.props.onChange}
              onFieldError={this.props.onFieldError}
              forceRecordUpdate={this.props.forceRecordUpdate}
              onBlur={this.props.onBlur}
              record={record}
              savedDisplayData={selectedResult?.displayData}
              isEditMode={isEditMode}
              showPreview={showPreview}
              stickyHeader={stickyHeader}
            />
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  searchResults: selectTripadvisorResults(state)
});

const mapDispatchToProps = {
  clearTripadvisorResults
};

export default connect(mapStateToProps, mapDispatchToProps)(TripAdvisorInput);
